 
import {
  ChangeDetectorRef,
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  ElementRef,
  EventEmitter,
  forwardRef,
  HostListener,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  AbstractControl,
  ControlValueAccessor,
  FormsModule,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
  ValidationErrors,
  Validator,
  Validators,
} from '@angular/forms';
import {
  IconDirective,
  SharedScrollEndNotifierDirective,
  SpinnerComponent,
  TextboxVariant,
} from '@seech/shared-ng';
import { MdbDropdownModule } from 'mdb-angular-ui-kit/dropdown';
import { MdbRippleModule } from 'mdb-angular-ui-kit/ripple';
import { MdbSelectModule } from 'mdb-angular-ui-kit/select';
import { SelectOption } from '../select/select.component';
import { ControlsConfigService } from '../../injection/controls-config.service';
import { TEXTBOX_VARIANT } from '../../constants';

export interface NestedSelectOption {
  title: string;
  options: SelectOption[];
  label?: string;
  value?: any;
  hideOption?: boolean;
}
@Component({
  selector: 'seech-nested-select',
  standalone: true,
  imports: [
    CommonModule,
    MdbDropdownModule,
    MdbRippleModule,
    FormsModule,
    ReactiveFormsModule,
    MdbSelectModule,
    IconDirective,
    SharedScrollEndNotifierDirective,
    SpinnerComponent
  ],
  templateUrl: './nested-select.component.html',
  styleUrl: './nested-select.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => NestedSelectComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => NestedSelectComponent),
      multi: true,
    },
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class NestedSelectComponent
  implements OnInit, OnChanges, ControlValueAccessor, Validator
{
  filteredOptions: NestedSelectOption[] = [];

  @Input() selectedValue: any = '';
  @Input({ required: true }) options: NestedSelectOption[] = [];
  @Input() label?: string;
  @Input() id = '';
  @Input() isContentLoading = false;
  @Input() disabled = false;
  @Input() readonly = false;
  @Input() editable = false;
  @Input() showSelected = false;
  @Input() disableSearch = false;
  @Input() autocomplete: 'on' | 'off' = 'off';
  @Input() threshold = 100;
  @Input() placeholder = '';
  @Input() readonlyplaceholder = '';
  @Input() variant!: TextboxVariant;

  @Output() selectedValueChange = new EventEmitter<any>();
  @Output() closed = new EventEmitter<boolean>();
  @Output() opened = new EventEmitter<boolean>();
  @Output() selected = new EventEmitter<NestedSelectOption>();
  @Output() reachedEndOfList = new EventEmitter<void>();
  @Output() searchTermChange = new EventEmitter<string>();

  textboxVariants = TEXTBOX_VARIANT;
  showDropdown = false;
  @ViewChild('dropdownMenu') dropdownMenu!: ElementRef;
  @ViewChild('searchInput') searchInput!: ElementRef;
  private focusedOptionIndex = -1;

  private onChange = (value: any) => {};
  private onTouched = () => {};

  constructor(
    private eRef: ElementRef,
    private configService: ControlsConfigService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['options']) {
      this.filteredOptions = [...this.options];
    }
  }

  ngOnInit(): void {
    if (!this.variant) {
      const config = this.configService.getConfig();
      this.variant = config?.textboxVariant ? config.textboxVariant : 'classic';
    }
  }

  get selectedLabel(): string {
    if (!this.selectedValue) return '';

    return this.options.reduce((label, option) => {
      if (label) return label;
      if (option.options) {
        const nestedOption = option.options.find(
          (o) => o.value === this.selectedValue
        );
        return nestedOption?.label || '';
      }
      return '';
    }, '');
  }

  selectOption(option: any) {
    this.selectedValue = option.value;
    this.onChange(option.value);
    this.onTouched();
    this.selectedValueChange.emit(option);
    this.selected.emit(option);
    this.cdr.detectChanges();
    this.toggleDropdown();
  }

  onInput(event: any) {
    const searchTerm = event.target.value.toLowerCase();
    this.showDropdown = true;

    this.filteredOptions = this.options
      .map((option) => {
        if (option.options) {
          const filteredGroup = option.options.filter((o) =>
            o.label.toLowerCase().includes(searchTerm)
          );
          return { ...option, options: filteredGroup };
        }
        return option.label?.toLowerCase().includes(searchTerm) ? option : null;
      })
      .filter(
        (option) =>
          option && (option.label || (option.options?.length ?? 0) > 0)
      ) as NestedSelectOption[];

    this.searchTermChange.emit(searchTerm);

    // Hide the dropdown if no results are found
    if (this.filteredOptions.length === 0) {
      this.showDropdown = false;
    }
  }

  onSearchInput(event: any) {
    const searchTerm = event.target.value.toLowerCase();

    this.filteredOptions = this.options
      .map((option) => {
        if (option.options) {
          const filteredGroup = option.options.filter((o) =>
            o.label.toLowerCase().includes(searchTerm)
          );
          return { ...option, options: filteredGroup };
        }
        return option.label?.toLowerCase().includes(searchTerm) ? option : null;
      })
      .filter(
        (option) =>
          option && (option.label || (option.options?.length ?? 0) > 0)
      ) as NestedSelectOption[];

    this.searchTermChange.emit(searchTerm);
  }

  writeValue(value: any): void {
    if (value) {
      this.selectedValue = value;
    }
  }

  toggleDropdown() {
    this.showDropdown = !this.showDropdown;
    this.focusedOptionIndex = -1;
    this.showDropdown === true
      ? this.opened.emit(true)
      : this.opened.emit(false);
    
    this.cdr.detectChanges();
    
    // Focus search input when dropdown opens and clear search when it closes
    if (this.showDropdown && !this.disableSearch) {
      setTimeout(() => {
        if (this.searchInput && this.searchInput.nativeElement) {
          this.searchInput.nativeElement.focus();
        }
      }, 50);
    } else if (!this.showDropdown && !this.disableSearch && this.searchInput) {
      // Clear search and reset filtered options when closing
      this.searchInput.nativeElement.value = '';
      this.filteredOptions = [...this.options];
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  validate(control: AbstractControl): ValidationErrors | null {
    // return this.selectedValue ? null : { required: true };
    const value = control.value;
    const valueIsRequired = control.hasValidator(Validators.required);
    const requiredError = { required: true };
    if (!value && valueIsRequired) {
      return requiredError;
    }
    return null;
  }

  @HostListener('document:click', ['$event.target'])
  public onClick(targetElement: HTMLElement) {
    const clickedInside = this.eRef.nativeElement.contains(targetElement);
    if (!clickedInside && this.showDropdown) {
      this.toggleDropdown();
      this.closed.emit(true);
      this.opened.emit(false);
    }
  }

  @HostListener('keydown', ['$event'])
  public onKeyDown(event: KeyboardEvent) {
    if (!this.showDropdown || this.readonly || this.disabled) return;

    const allOptions = this.getAllSelectableOptions();
    
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.focusedOptionIndex = Math.min(
          this.focusedOptionIndex + 1,
          allOptions.length - 1
        );
        this.scrollToFocusedOption();
        break;

      case 'ArrowUp':
        event.preventDefault();
        this.focusedOptionIndex = Math.max(this.focusedOptionIndex - 1, 0);
        this.scrollToFocusedOption();
        break;

      case 'Enter':
        event.preventDefault();
        if (this.focusedOptionIndex >= 0 && allOptions[this.focusedOptionIndex]) {
          this.selectOption(allOptions[this.focusedOptionIndex]);
        }
        break;

      case 'Escape':
        event.preventDefault();
        if (this.showDropdown) {
          this.toggleDropdown();
          this.closed.emit(true);
        }
        break;
    }
  }

  private getAllSelectableOptions(): SelectOption[] {
    return this.filteredOptions.reduce((acc: SelectOption[], option) => {
      if (option.options) {
        const selectableOptions = option.options.filter(
          (opt) => !opt.hideOption && (this.showSelected || this.selectedValue !== opt.value)
        );
        return [...acc, ...selectableOptions];
      }
      return acc;
    }, []);
  }

  private scrollToFocusedOption() {
    if (this.dropdownMenu && this.focusedOptionIndex >= 0) {
      setTimeout(() => {
        const options = this.dropdownMenu.nativeElement.querySelectorAll('.sch-select-option');
        const focusedOption = options[this.focusedOptionIndex] as HTMLElement;
        if (focusedOption) {
          focusedOption.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
          focusedOption.focus();
        }
      });
    }
  }

  onScrollEnd() {
    this.reachedEndOfList.emit();
  }
}
